import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../../../config';
import { getCachedImageBlob } from '../utils/imageBlobCache';

/**
 * Loads protected /api/files/* resources with Bearer token into a blob URL.
 */
const AuthMedia = ({
    src,
    alt = '',
    className,
    as = 'img',
    onClick,
    onLoad,
    priority = 'auto',
}) => {
    const [blobUrl, setBlobUrl] = useState('');
    const [error, setError] = useState(false);
    const eager = priority === 'high' || as === 'audio' || as === 'video';
    const [shouldLoad, setShouldLoad] = useState(eager);
    const hostRef = useRef(null);

    useEffect(() => {
        setShouldLoad(eager);
        if (eager || !src) return undefined;
        const target = hostRef.current;
        if (!target || typeof IntersectionObserver === 'undefined') {
            setShouldLoad(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                setShouldLoad(true);
                observer.disconnect();
            },
            { rootMargin: '600px 0px' }
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [src, priority, eager]);

    useEffect(() => {
        let objectUrl = '';
        let cancelled = false;

        setBlobUrl('');
        setError(false);

        const load = async () => {
            if (!src || !shouldLoad) return;
            const full = src.startsWith('http') ? src : `${API_URL}${src}`;
            if (full.startsWith('blob:') || full.startsWith('data:')) {
                if (!cancelled) setBlobUrl(full);
                return;
            }
            try {
                const token = localStorage.getItem('token');
                const blob = await getCachedImageBlob(full, async () => {
                    const res = await fetch(full, {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                    });
                    if (!res.ok) throw new Error('load failed');
                    return res.blob();
                });
                if (cancelled) return;
                const blobType = (blob.type || '').toLowerCase();
                if (as === 'audio' && (blobType.includes('json') || blob.size < 64)) {
                    throw new Error('not audio');
                }
                objectUrl = URL.createObjectURL(blob);
                setBlobUrl(objectUrl);
            } catch (e) {
                if (cancelled) return;
                setError(true);
            }
        };

        load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [src, shouldLoad, as]);

    if (error) {
        return (
            <div
                ref={hostRef}
                className={className}
                style={{
                    minHeight: 120,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                Не удалось загрузить
            </div>
        );
    }
    if (!blobUrl) {
        return (
            <div
                ref={hostRef}
                className={className}
                style={{
                    minHeight: as === 'audio' ? 48 : 200,
                    width: '100%',
                    opacity: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.06)',
                }}
                aria-hidden
            >
                …
            </div>
        );
    }
    if (as === 'audio') {
        return <audio src={blobUrl} controls className={className} />;
    }
    if (as === 'video') {
        return (
            <video
                src={blobUrl}
                controls
                className={className}
                onClick={onClick}
                style={{ display: 'block', width: '100%', height: 'auto' }}
            />
        );
    }
    if (as === 'img') {
        return (
            <img
                src={blobUrl}
                alt={alt}
                className={className}
                onClick={onClick}
                onLoad={onLoad}
                loading={priority === 'high' ? 'eager' : 'lazy'}
                fetchPriority={priority === 'high' ? 'high' : 'auto'}
                style={{ display: 'block', width: '100%', height: 'auto' }}
            />
        );
    }
    return blobUrl;
};

export default AuthMedia;
